/**
 * Shared zod schemas + types for the LLM provider surface (M2 Phase 7).
 *
 * Source of truth for the LLM settings shape consumed by both
 * `@aris/api` (supervisor settings panel, provider registry, bot fact
 * extractor) and `@aris/web` (settings UI). The Pre-Wave (R7.0) commits
 * these schemas before any provider implementation lands so Wave 1
 * BE coders, Wave 4 FE coder, and Wave 3 bot coder all compile against
 * an identical contract.
 *
 * Stability promises:
 *   - LlmProviderName is a closed enum — adding a provider is a SCHEMA
 *     change (so consumers update exhaustive switches at compile time).
 *   - ExtractedFactSchema intentionally has NO `project` field (R2
 *     scope-lock): project is resolved up the call stack from FSM
 *     conversation state, never from LLM output.
 */
import { z } from 'zod';
import { ruEnum } from './lib/zod-ru';

export const LlmProviderName = z.enum(
  [
    'closerouter',
    'anthropic',
    'openai',
    'gigachat',
    'yandexgpt',
    // r6.25 — BotHub (https://bothub.chat) is an OpenAI-compatible,
    // Russian-payable gateway. Wired via the generic OpenAI-shape chat
    // adapter (see apps/api/src/llm/providers/openai-compat.provider.ts).
    'bothub',
  ],
  ruEnum('Выберите поставщика'),
);
export type LlmProviderName = z.infer<typeof LlmProviderName>;

export const LlmSettingsSchema = z
  .object({
    provider: LlmProviderName.default('closerouter'),
    endpoint: z.string().trim().max(255).default('https://api.closerouter.dev/v1'),
    apiKey: z.string().max(1024).default(''),
    model: z.string().trim().max(120).default(''),
    enabled: z.coerce.boolean().default(false),
    maxTokens: z.coerce.number().int().min(64).max(8192).default(1024),
    temperature: z.coerce.number().min(0).max(2).default(0),
    /**
     * R2 (C5) — per-user daily call cap. Enforced server-side in the
     * provider facade; supervisor settings panel reads/writes the value.
     */
    dailyCapPerUser: z.coerce.number().int().min(1).max(10000).default(100),
  })
  .strict();
export type LlmSettings = z.infer<typeof LlmSettingsSchema>;

/**
 * The bot's fact-extractor output shape. R2 / scope-locked:
 *   - NO `project` key (resolved via FSM state).
 *   - `hours` is float-fuzzy guarded by `.transform(round to 0.5)` then
 *     `.refine` to [0.5, 24]. This catches LLM drift (e.g. "3.51 hours")
 *     before it hits the WorkloadFact CHECK constraint.
 *
 * ─── ⚠️ Ф5 · В9-b — ПОЛЯ `date` ЗДЕСЬ БОЛЬШЕ НЕТ, И ЭТО РЕШЕНИЕ, А НЕ ЧИСТКА ──
 *
 * Договор — `{ hours, description }`. Основание — **Р-89, «Отчёт через бота
 * идёт от вида к разделу, а дата не спрашивается и не извлекается»**,
 * положение 1 и поправка «поле дня убирается из договора извлечения целиком»
 * (владелец сперва распорядился поле ОСТАВИТЬ и переменил решение после
 * предъявленного разбора — ответ **«Б»**). Отчёт через бота подаётся ТОЛЬКО за
 * текущую дату; задним числом часы вносятся ТОЛЬКО через веб, и **веб этим не
 * затронут** — разбором моделью он не пользуется вовсе.
 *
 * ⚠️ **ЦЕНА НАЗВАНА В САМОМ РЕШЕНИИ И НЕ СМЯГЧАЕТСЯ ЗДЕСЬ:** слово «вчера» в
 * рассказе теперь ни на что не влияет и никем не замечается. Прежде извлечённая
 * дата служила ДЕТЕКТОРОМ расхождения (Р-71, граница 4 — отказ с названной
 * датой и кнопкой «Подать за сегодня»); детектор снят ВМЕСТЕ СО СВОИМ
 * НОСИТЕЛЕМ. Кто станет возвращать поле «чтобы предупреждать» — возвращает
 * отменённое решение, а не улучшает это.
 *
 * ⚠️ **СХЕМА `.strict()`, И ЭТО НЕСУЩЕЕ.** Лишний ключ в ответе модели валит
 * разбор ЦЕЛИКОМ, а не игнорируется.
 *
 * ⚠️ **ОСТАТОЧНЫЙ РИСК НАЗВАН, А НЕ СМЯГЧЁН: модель, всё-таки приславшая
 * `date`, роняет извлечение целиком.** Промпт просит день не называть прямо
 * (`llm-prompt.ru.ts`), но послушание модели — не гарантия. Ослабить схему до
 * `.strip()` значило бы переписать договор, которого никто не переписывал:
 * Р-89 называет `.strict()` частью предмета. Поэтому оставлено как есть, а
 * риск записан: отказ будет ГРОМКИМ («не удалось разобрать сообщение»), а не
 * тихим, и это тот вид отказа, который волна и предпочитает. Поэтому промпт
 * (`apps/api/src/llm/llm-prompt.ru.ts`) и эта схема правятся ОДНИМ коммитом:
 * схема без `date` при промпте, всё ещё просящем дату, даёт не «поле
 * игнорируется», а мёртвое извлечение на каждом сообщении.
 */
export const ExtractedFactSchema = z
  .object({
    hours: z
      .number()
      .transform((v) => Math.round(v * 2) / 2)
      .refine((v) => v >= 0.5 && v <= 24, {
        message: 'Укажите от 0,5 до 24 часов с шагом полчаса',
      }),
    description: z.string().trim().max(2000).optional(),
  })
  .strict();
export type ExtractedFact = z.infer<typeof ExtractedFactSchema>;

/**
 * Normalise an operator-supplied OpenAI-compatible endpoint to a
 * canonical base that always ends in `/v1` (no trailing slash). Callers
 * append their leaf path (`/models`, `/chat/completions`) to the result.
 *
 * r6.25 (DA FIX-1): the models-proxy `/models` URL builder and the chat
 * `/chat/completions` URL builder MUST agree on `/v1` handling, or a
 * supervisor who saves an endpoint WITHOUT the trailing `/v1` (e.g.
 * `https://bothub.chat/api/v2/openai`) gets a healthy-looking model
 * dropdown (proxy re-adds `/v1`) while every real extraction 404s
 * (`…/openai/chat/completions`). This single source of truth makes the
 * two builders impossible to diverge.
 *
 * Behaviour:
 *   - validates via `new URL`; throws on malformed input or non-http(s).
 *   - strips any trailing slashes, then strips ONE trailing `/v1` leaf,
 *     then re-appends `/v1` — so both `…/openai/v1` and `…/openai`
 *     collapse to `…/openai/v1`, and `https://api.closerouter.dev/v1`
 *     stays `https://api.closerouter.dev/v1`.
 *   - preserves any path PREFIX before the trailing `/v1`
 *     (`https://bothub.chat/api/v2/openai/v1` → same).
 *
 * Returns the normalised base as a string (origin + path, no query/hash).
 */
export function normalizeOpenAiBaseToV1(endpoint: string): string {
  const trimmed = endpoint.trim();
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    throw new Error(`LLM endpoint is not a valid URL: ${truncateForError(trimmed, 80)}`);
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    throw new Error(`LLM endpoint must use http(s): ${parsed.protocol}`);
  }
  let pathname = parsed.pathname;
  while (pathname.endsWith('/')) pathname = pathname.slice(0, -1);
  if (pathname.endsWith('/v1')) pathname = pathname.slice(0, -3);
  // Drop query/hash — the leaf paths never carry either.
  return `${parsed.origin}${pathname}/v1`;
}

function truncateForError(s: string, n: number): string {
  return s.length <= n ? s : `${s.slice(0, n)}…`;
}
